import { rewordValidationMessages } from '#api/errors.ts';
import type { OIDCProvider } from '#dn-types/oidc-provider.ts';
import { del, get, post, put } from './fetch.ts';

// GET /oidc-providers
export type GetOIDCProvidersResult = OIDCProvider[];
export function getOIDCProviders(init?: RequestInit) {
  return get<GetOIDCProvidersResult>('/v1/oidc-providers', init);
}

// GET /oidc-providers/:id
export type GetOIDCProviderResult = OIDCProvider;
export function getOIDCProvider(id: string, init?: RequestInit) {
  return get<GetOIDCProviderResult>(`/v1/oidc-providers/${encodeURIComponent(id)}`, init);
}

// PUT /oidc-providers/:id
export type EditOIDCProviderVariables = {
  id: string;
  values: {
    /** Requirement: <= 255 chars */
    clientID: string;
    /** Requirement: <= 255 chars */
    clientSecret: string | null;
    /** Requirement: begins with https:// */
    issuerURL: string;
    enabled: boolean;
    /** Requirement: must be a verified domain if enabled is true */
    domainID: string | null;
  };
};
export type EditOIDCProviderResult = OIDCProvider;
export function editOIDCProvider(input: EditOIDCProviderVariables) {
  const { id, values } = input;
  return put<EditOIDCProviderResult>(`/v1/oidc-providers/${encodeURIComponent(id)}`, values).catch((err: unknown) => {
    rewordOIDCProviderErrors(err, values);
  });
}

// POST /oidc-providers
export type AddOIDCProviderVariables = {
  /** Requirement: <= 255 chars */
  clientID: string;
  /** Requirement: <= 255 chars */
  clientSecret: string;
  /** Requirement: begins with https:// */
  issuerURL: string;
  enabled: boolean;
  /** Requirement: must be a verified domain if enabled is true */
  domainID: string | null;
};
export type AddOIDCProviderResult = OIDCProvider;
export function addOIDCProvider(formData: AddOIDCProviderVariables) {
  return post<AddOIDCProviderResult>('/v1/oidc-providers', formData).catch((err: unknown) => {
    rewordOIDCProviderErrors(err, formData);
  });
}

// DELETE /oidc-providers
export type DeleteOIDCProviderResult = Record<string, never>;
export function deleteOIDCProvider(id: string) {
  return del<DeleteOIDCProviderResult>(`/v1/oidc-providers/${encodeURIComponent(id)}`);
}

function rewordOIDCProviderErrors(
  err: unknown,
  { issuerURL }: AddOIDCProviderVariables | EditOIDCProviderVariables['values']
): never {
  rewordValidationMessages(err, (details) => {
    if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
      return 'Only one admin identity provider can be created';
    }
    if (details.path === 'issuerURL' && details.code === 'ERR_OIDC_DISCOVERY_VALIDATION_FAILURE') {
      return `Could not find a valid discovery document at ${issuerURL.replace(
        /\/$/,
        ''
      )}/.well-known/openid-configuration`;
    }
    if (details.path === 'issuerURL' && details.code === 'ERR_OIDC_ISSUER_DISCOVERY_MISMATCH') {
      return `The "issuer" field found in the discovery document ${issuerURL.replace(
        /\/$/,
        ''
      )}/.well-known/openid-configuration does not match the one provided`;
    }

    if (details.path === 'issuerURL' && details.code === 'ERR_INVALID_HTTPS_URL') {
      return 'Please provide a valid URL';
    }

    if (details.path === 'domainID' && details.code === 'ERR_INVALID_REFERENCE') {
      return 'Please choose a valid domain';
    }
    if (details.path === 'domainID' && details.code === 'ERR_DOMAIN_ALREADY_ENABLED') {
      return 'This domain is already used by another provider';
    }
    if (details.path === 'domainID' && details.code === 'ERR_DOMAIN_NOT_VERIFIED') {
      return 'Please disable the provider before assigning an unverified domain';
    }
  });
}
